import { useEffect, useRef, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { pushBackHandler } from '@/app/backButton';
import { AnimatePresence, m } from 'framer-motion';
import { cn } from '@/lib/cn';

export interface SheetProps {
  open: boolean;
  onClose: () => void;
  title?: ReactNode;
  children: ReactNode;
  /** Модальный лист нельзя закрыть тапом по подложке. */
  dismissible?: boolean;
  className?: string;
}

/**
 * Нижний лист. Используется для инструкции, подсказки, подтверждения смены
 * профиля и запроса разрешения на уведомления.
 */
export function Sheet({ open, onClose, title, children, dismissible = true, className }: SheetProps) {
  /**
   * Закрытие берётся из ref, а не из зависимостей эффекта.
   *
   * Экран передаёт новую функцию на каждый рендер, и эффект пересобирался бы
   * следом: обработчик снимался и ставился заново, а вместе с ним мост
   * прятал и показывал системную кнопку. На части клиентов после такой
   * чехарды кнопка оставалась видимой, но переставала отвечать.
   */
  const closeRef = useRef(onClose);
  closeRef.current = onClose;

  useEffect(() => {
    if (!open) return undefined;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && dismissible) closeRef.current();
    };
    window.addEventListener('keydown', onKeyDown);

    // Системная кнопка «Назад» закрывает лист, а не уводит с экрана: иначе
    // из открытого листа она выглядит сломанной
    const releaseBack = pushBackHandler(() => closeRef.current());

    return () => {
      document.body.style.overflow = previous;
      window.removeEventListener('keydown', onKeyDown);
      releaseBack();
    };
  }, [open, dismissible]);

  return createPortal(
    <AnimatePresence>
      {open ? (
        <div className="fixed inset-0 z-40 flex items-end justify-center" role="dialog" aria-modal="true">
          {/*
            Подложка — украшение с удобным жестом закрытия: доступное действие
            даёт крестик в шапке, а два одинаковых «Закрыть» подряд только
            путают чтение с экрана
          */}
          <m.div
            aria-hidden
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => dismissible && closeRef.current()}
            className="absolute inset-0 bg-black/45 backdrop-blur-[2px]"
          />
          <m.div
            initial={{ y: '100%' }}
            animate={{ y: 0 }}
            exit={{ y: '100%' }}
            transition={{ type: 'spring', damping: 32, stiffness: 320 }}
            className={cn(
              'bd-scroll relative w-full max-w-[520px] rounded-t-sheet bg-surface px-4 pb-[calc(20px+var(--bd-safe-bottom))] pt-3 shadow-sheet',
              'max-h-[88vh]',
              className,
            )}
          >
            <span className="mx-auto mb-4 block h-1 w-12 rounded-full bg-ink/20" aria-hidden />

            {/*
              Закрытие всегда под рукой в шапке: содержимое листа бывает выше
              экрана, и кнопка внизу оказывается за его пределами, а системная
              кнопка «Назад» есть не в каждом клиенте
            */}
            {dismissible ? (
              <button
                type="button"
                onClick={() => closeRef.current()}
                aria-label="Закрыть"
                className="bd-focus absolute right-3 top-3 flex h-9 w-9 items-center justify-center rounded-full bg-surface-2 text-muted"
              >
                <X className="h-4 w-4" aria-hidden />
              </button>
            ) : null}

            {title ? (
              <h2 className="mb-2 pr-10 text-[19px] font-medium tracking-[-0.02em]">{title}</h2>
            ) : null}
            {children}
          </m.div>
        </div>
      ) : null}
    </AnimatePresence>,
    document.body,
  );
}
